/**
 * EventTimelineWidget — Scrollable structured event log
 * Shows: Color-coded event list (CRASH, RESTART, STARTUP, etc.) with filters
 * Collapsible — click header to expand/collapse
 * Refresh: 60s
 * CSS prefix: etw-
 */

import { useState, useEffect, useCallback } from 'react'
import { fetchEvents } from '../lib/api'
import { List, RefreshCw, ChevronRight } from 'lucide-react'
import './EventTimelineWidget.css'

const EVENT_TYPES = ['ALL', 'CRASH', 'RESTART', 'ERROR', 'STARTUP', 'SHUTDOWN', 'HEARTBEAT', 'WATCHDOG']

const EVENT_COLORS = {
  CRASH: '#ff4444',
  RESTART: '#FF9800',
  ERROR: '#ff4444',
  STARTUP: '#00C853',
  SHUTDOWN: '#FFD600',
  HEARTBEAT: '#00B8D4',
  WATCHDOG: '#7C4DFF',
  ABANDON: '#FF0055',
  CONFIG: '#7A8B9A',
}

function EventTimelineWidget() {
  const [events, setEvents] = useState([])
  const [filter, setFilter] = useState('ALL')
  const [status, setStatus] = useState('unknown')
  const [proxyStatus, setProxyStatus] = useState(null)
  const [lastCheck, setLastCheck] = useState(null)
  const [loading, setLoading] = useState(false)
  const [expanded, setExpanded] = useState(false)

  const fetchData = useCallback(async () => {
    setLoading(true)
    const params = { limit: 50 }
    if (filter !== 'ALL') params.type = filter

    const res = await fetchEvents(params)
    setProxyStatus(res.status)

    if (res.status === 'ok' && res.data) {
      const eventList = Array.isArray(res.data) ? res.data : (res.data.events || [])
      setEvents(eventList)
      setStatus(eventList.length > 0 ? 'online' : 'warning')
    } else {
      setEvents([])
      setStatus(res.status === 'sleeping' ? 'sleeping' : 'offline')
    }

    setLoading(false)
    setLastCheck(new Date().toLocaleTimeString())
  }, [filter])

  useEffect(() => {
    fetchData()
    const interval = setInterval(fetchData, 60000)
    return () => clearInterval(interval)
  }, [fetchData])

  const formatTime = (isoStr) => {
    if (!isoStr) return '--'
    try {
      const d = new Date(isoStr)
      const now = new Date()
      const diffMs = now - d
      const diffMin = Math.floor(diffMs / 60000)

      if (diffMin < 1) return 'just now'
      if (diffMin < 60) return `${diffMin}m ago`

      const diffHr = Math.floor(diffMin / 60)
      if (diffHr < 24) return `${diffHr}h ago`

      return d.toLocaleDateString([], { month: 'short', day: 'numeric' }) +
        ' ' + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    } catch { return isoStr }
  }

  return (
    <div className={`widget-card ${status}`}>
      <div className="widget-header etw-header-clickable" onClick={() => setExpanded(!expanded)}>
        <ChevronRight size={14} className={`etw-chevron ${expanded ? 'rotated' : ''}`} />
        <List size={16} className="widget-icon" />
        <span className="widget-title">Event Timeline</span>
        <span className="etw-count">{events.length} events</span>
        <button className={`refresh-btn ${loading ? 'spinning' : ''}`} onClick={(e) => { e.stopPropagation(); fetchData() }} disabled={loading}>
          <RefreshCw size={12} />
        </button>
      </div>

      {expanded && (
        <div className="widget-body">
          {/* Filter buttons */}
          <div className="etw-filters">
            {EVENT_TYPES.map(type => (
              <button
                key={type}
                className={`etw-filter-btn ${filter === type ? 'active' : ''}`}
                onClick={() => setFilter(type)}
                style={filter === type && type !== 'ALL' ? { borderColor: EVENT_COLORS[type] || '#00B8D4', color: EVENT_COLORS[type] || '#00B8D4' } : {}}
              >
                {type === 'ALL' ? 'All' : type}
              </button>
            ))}
          </div>

          {proxyStatus !== 'ok' && events.length === 0 && (
            <div className={proxyStatus === 'sleeping' ? 'widget-sleeping' : 'widget-empty'}>
              <div>{proxyStatus === 'sleeping' ? 'Space is sleeping...' : 'Unable to fetch events'}</div>
            </div>
          )}

          {/* Event list */}
          {events.length > 0 && (
            <div className="etw-list">
              {events.map((evt, i) => (
                <div key={evt.event_id || i} className="etw-row">
                  <div
                    className="etw-type-indicator"
                    style={{ backgroundColor: EVENT_COLORS[evt.type] || '#7A8B9A' }}
                  />
                  <div className="etw-content">
                    <div className="etw-row-header">
                      <span
                        className="etw-type-badge"
                        style={{ color: EVENT_COLORS[evt.type] || '#7A8B9A' }}
                      >
                        {evt.type}
                      </span>
                      <span className="etw-source">{evt.source}</span>
                      <span className="etw-time">{formatTime(evt.timestamp)}</span>
                    </div>
                    {evt.message && (
                      <div className="etw-message">{evt.message}</div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {proxyStatus === 'ok' && events.length === 0 && (
            <div className="widget-empty">No events found for this filter</div>
          )}

          <div className="widget-footer">
            <span>{lastCheck ? `Checked: ${lastCheck}` : 'Loading...'}</span>
          </div>
        </div>
      )}
    </div>
  )
}

export default EventTimelineWidget
